<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<script setup lang="ts">
// CKEditor imports
import { Ckeditor } from "@ckeditor/ckeditor5-vue";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { EditorConfig } from "@ckeditor/ckeditor5-core";

import {
  icons,
  ClassicEditor,
  Alignment,
  Autoformat,
  Bold,
  Code,
  Italic,
  Strikethrough,
  Subscript,
  Superscript,
  Underline,
  BlockQuote,
  CloudServices,
  CodeBlock,
  Essentials,
  FindAndReplace,
  Font,
  Heading,
  HorizontalLine,
  GeneralHtmlSupport,
  AutoImage,
  Image,
  ImageCaption,
  ImageInsert,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Base64UploadAdapter,
  PictureEditing,
  Indent,
  IndentBlock,
  TextPartLanguage,
  AutoLink,
  Link,
  LinkImage,
  List,
  ListProperties,
  TodoList,
  MediaEmbed,
  Mention,
  PageBreak,
  Paragraph,
  PasteFromOffice,
  RemoveFormat,
  SpecialCharacters,
  Style,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  WordCount,
  Plugin,
  ButtonView,
  ToolbarConfigItem,
} from "ckeditor5";

// CKEditor CSS
import "ckeditor5/ckeditor5.css";

// Vue imports
import { computed, ref, watch } from "vue";
import { globals } from "@/main";

// Global config imports
import { REDUCED_MATERIAL_COLORS } from "./config/colors";
import { useI18n } from "vue-i18n";

const MediaTypeEnumEnum = {
  IMAGE: "IMAGE",
  VIDEO: "VIDEO",
};

// Emit events
const emits = defineEmits(["update:modelValue", "update", "returned"]);

// Editor-related reactive variables
const editorInstance = ref<ClassicEditor | null>(null);
const editorData = ref("<p></p>");
const mediaFormat = ref(MediaTypeEnumEnum.IMAGE);
const isToggleMedia = ref(false);

// Props
const props = defineProps({
  readonly: {
    type: Boolean,
    default: false,
  },
  editorOption: {
    type: Array as () => ToolbarConfigItem[],
    default: () => [
      "fontFamily",
      "heading",
      "fontSize",
      "fontColor",
      "fontBackgroundColor",
      "bold",
      "italic",
      "underline",
      "strikethrough",
      "superscript",
      "subscript",
      "|",
      "alignment",
      "bulletedList",
      "numberedList",
      "todoList",
      "outdent",
      "indent",
      "|",
      "insertTable",
      "Link",
      "insertImage",
      "ImageMedia",
      "mediaEmbed",
      "blockQuote",
      "horizontalLine",
      "pageBreak",
      "|",
      "findAndReplace",
      "selectAll",
      "removeFormat",
    ],
  },
  modelValue: String,
  isError: {
    type: Boolean,
    default: false,
  },
  placeholder: { type: String, default: "" },
});

// Define custom ImageMedia plugin
class ImageMedia extends Plugin {
  init() {
    const editor = this.editor;
    editor.ui.componentFactory.add("ImageMedia", () => {
      const button = new ButtonView();
      button.set({
        label: "Insert Image",
        icon: icons.image,
        tooltip: true,
      });
      button.on("execute", () => {
        onToggleMediaUpload(MediaTypeEnumEnum.IMAGE);
      });
      return button;
    });
  }
}

const { t } = useI18n();
const placeholderKey = ref(t(props.placeholder));

// Editor configuration
const editorConfig = ref<any>({
  // replace any with EditorConfig
  plugins: [
    Alignment,
    Autoformat,
    AutoImage,
    AutoLink,
    BlockQuote,
    Bold,
    CloudServices,
    Code,
    CodeBlock,
    Essentials,
    FindAndReplace,
    Font,
    GeneralHtmlSupport,
    Heading,
    HorizontalLine,
    Image,
    ImageInsert,
    ImageCaption,
    ImageResize,
    ImageStyle,
    ImageToolbar,
    ImageUpload,
    Base64UploadAdapter,
    Indent,
    IndentBlock,
    Italic,
    Link,
    LinkImage,
    List,
    ListProperties,
    MediaEmbed,
    Mention,
    PageBreak,
    Paragraph,
    PasteFromOffice,
    PictureEditing,
    RemoveFormat,
    SpecialCharacters,
    Strikethrough,
    Style,
    Subscript,
    Superscript,
    Table,
    TableCaption,
    TableCellProperties,
    TableColumnResize,
    TableProperties,
    TableToolbar,
    TextPartLanguage,
    TextTransformation,
    TodoList,
    Underline,
    WordCount,
    ImageMedia,
  ],
  toolbar: {
    shouldNotGroupWhenFull: true,
    items: props.editorOption as ToolbarConfigItem[],
  },
  fontFamily: { supportAllValues: true },
  fontSize: {
    options: [10, 12, 14, "default", 18, 20, 22],
    supportAllValues: true,
  },
  fontColor: { columns: 12, colors: REDUCED_MATERIAL_COLORS },
  fontBackgroundColor: { columns: 12, colors: REDUCED_MATERIAL_COLORS },
  heading: {
    options: [
      { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
      {
        model: "heading1",
        view: "h1",
        title: "Heading 1",
        class: "ck-heading_heading1",
      },
      {
        model: "heading2",
        view: "h2",
        title: "Heading 2",
        class: "ck-heading_heading2",
      },
      {
        model: "heading3",
        view: "h3",
        title: "Heading 3",
        class: "ck-heading_heading3",
      },
      {
        model: "heading4",
        view: "h4",
        title: "Heading 4",
        class: "ck-heading_heading4",
      },
      {
        model: "heading5",
        view: "h5",
        title: "Heading 5",
        class: "ck-heading_heading5",
      },
      {
        model: "heading6",
        view: "h6",
        title: "Heading 6",
        class: "ck-heading_heading6",
      },
    ],
  },
  image: {
    resizeOptions: [
      {
        name: "resizeImage:original",
        label: "Default image width",
        value: null,
      },
      { name: "resizeImage:50", label: "50% page width", value: "50" },
      { name: "resizeImage:75", label: "75% page width", value: "75" },
      { name: "resizeImage:100", label: "100% page width", value: "100" },
    ],
    toolbar: [
      "imageTextAlternative",
      "toggleImageCaption",
      "|",
      "imageStyle:inline",
      "imageStyle:wrapText",
      "imageStyle:breakText",
      "|",
      "resizeImage",
      "linkImage",
    ],
    insert: { integrations: ["url"] },
  },
  placeholder: placeholderKey.value,
  list: { properties: { styles: true, startIndex: true, reversed: true } },
  link: {
    addTargetToExternalLinks: true,
  },
  table: {
    contentToolbar: [
      "tableColumn",
      "tableRow",
      "mergeTableCells",
      "tableCellProperties",
      "tableProperties",
    ],
  },
  mediaEmbed: { previewsInData: true },
});

function updateCKEditorPlaceholder(newPlaceholder: string) {
  const queryElement = document.querySelector("[data-placeholder]");

  if (queryElement) {
    queryElement.setAttribute("data-placeholder", newPlaceholder);
  }
}

watch(
  () => globals.$helper?.i18nLocale?.value,
  () => {
    const newPlaceholder = t(props.placeholder);
    updateCKEditorPlaceholder(newPlaceholder);
  },
  { immediate: true }
);

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) editorData.value = newValue;
  },
  { immediate: true }
);

watch(
  () => props.readonly,
  (newReadonlyState: boolean) => {
    // Ensure that editorConfig and toolbar exist
    if (!editorConfig.value || !editorConfig.value.toolbar) return;

    // Update toolbar based on readonly state
    if (newReadonlyState) {
      // Remove "ImageMedia" from the toolbar when readonly is true
      editorConfig.value.toolbar.items =
        editorConfig.value.toolbar.items.filter(
          (item: string) => item !== "ImageMedia"
        );
    } else {
      // Add "ImageMedia" to the toolbar if it's not already included
      if (!editorConfig.value.toolbar.items.includes("ImageMedia")) {
        editorConfig.value.toolbar.items.push("ImageMedia");
      }
    }
  },
  { immediate: true }
);

// Watch for changes in the error state
watch(
  () => props.isError,
  (newErrorState) => {
    if (editorInstance.value) {
      updateEditorContentClass(newErrorState);
    }
  },
  { immediate: true }
);

// Handle editor readiness
function onEditorReady(editor: ClassicEditor) {
  editorInstance.value = editor;

  // Update class on editor ready based on initial error state
  updateEditorContentClass(props.isError);
  updateEditorContentClassReadonly(props.readonly);
}

// Function to update the editor content class
function updateEditorContentClass(isError: boolean) {
  const editorContent = editorInstance.value?.ui.view.editable.element;
  if (editorContent) {
    if (isError) {
      editorContent.classList.add("border-error");
    } else {
      editorContent.classList.remove("border-error");
    }
  }
}

function updateEditorContentClassReadonly(readonly: boolean) {
  const editorContent = editorInstance.value?.ui.view.editable.element;
  if (editorContent) {
    if (readonly) {
      editorContent.classList.add("disable-content");
    } else {
      editorContent.classList.remove("disable-content");
    }
  }
}

// Toggle media upload
function onToggleMediaUpload(mediaType: string) {
  mediaFormat.value = mediaType;
  isToggleMedia.value = true;
}

// Handle media selection
function onSelectMedia(path: string) {
  const url = globals.$helper?.getFullImage(path);
  isToggleMedia.value = false;
  insertImageIntoEditor(url);
}

// Insert selected image into the editor
function insertImageIntoEditor(url: string) {
  if (editorInstance.value?.model) {
    editorInstance.value.model.change((writer) => {
      const imageElement = writer.createElement("imageBlock", { src: url });
      const selection = editorInstance.value?.model.document.selection;
      if (selection) {
        editorInstance.value?.model.insertContent(
          imageElement,
          selection.getFirstRange()
        );
      }
    });
  }
}

// Update model value when editor content changes
function updateModelValue() {
  emits("update", editorData.value || "");
}

const error = computed(() => {
  return props.isError;
});
</script>

<template>
  <div :class="{ 'editor-error': error }" class="ck-editor-container">
    <ckeditor
      v-model="editorData"
      id="editor"
      :editor="ClassicEditor"
      :config="editorConfig"
      :disabled="readonly"
      @input="updateModelValue"
      @ready="onEditorReady"
    />
    <c-media
      v-model:isToggle="isToggleMedia"
      :format="mediaFormat"
      custom
      @on-select="onSelectMedia"
    />
  </div>
</template>

<style src="./ckeditor-style.scss" lang="scss"></style>
<style lang="scss">
.editor-error {
  .ck-focused,
  .ck-blurred {
    border: 1px solid var(--danger) !important;
  }
}
</style>
